import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { ApiError } from '../api';

export interface User {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  avatarUrl?: string;
  bio?: string;
  followers?: number;
  following?: number;
  role?: string;
  walletBalance?: string;
  sourceLocale?: string;
  locale?: string;
  realName?: string;
  idNumber?: string;
  bankName?: string;
  bankCardNumber?: string;
  alipayAccount?: string;
  paypalEmail?: string;
  staff?: {
    staffId: string;
    staffType: string;
    department: string;
    actions: string[];
  } | null;
  followingPublic?: boolean;
  followersPublic?: boolean;
  favoritesUncategorizedPublic?: boolean;
  notificationPreferences?: {
    emailNotifications: boolean;
    pushNotifications: boolean;
    sessionAlerts: boolean;
    marketingEmails: boolean;
  };
  displayPreferences?: {
    theme: 'light' | 'dark' | 'system';
  };
  twoFactorEnabled?: boolean;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;

  // Actions
  login: (email: string, password: string, turnstileToken?: string) => Promise<{ twoFactorRequired: true; challengeToken: string } | { twoFactorRequired: false }>;
  verify2fa: (challengeToken: string, code?: string, recoveryCode?: string) => Promise<void>;
  register: (email: string, password: string, name: string, code: string) => Promise<void>;
  sendVerificationCode: (email: string) => Promise<void>;
  // OAuth 回跳落地：一次性 code 换登录态；bind 模式只完成绑定不落登录态
  loginWithOAuthCode: (code: string) => Promise<
    | { mode: 'login'; isNewUser: boolean }
    | { mode: 'bind'; identity: { id: string; provider: string } }
  >;
  logout: () => Promise<void>;
  clearError: () => void;
  setUser: (user: User) => void;
  refreshUser: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (email: string, password: string, turnstileToken?: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await fetch('/api/auth/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password, turnstileToken }),
            credentials: 'include',
          });

          if (!response.ok) {
            throw new ApiError(response.status, await response.json());
          }

          const data = await response.json();
          // 2FA 已启用：后端不签发 access token，改发半登录态 challengeToken
          if (data.twoFactorRequired) {
            set({ isLoading: false });
            return { twoFactorRequired: true, challengeToken: data.challengeToken as string };
          }
          set({ user: data.user, token: data.token, isAuthenticated: true, isLoading: false });
          // login/register 返回的是精简 user（不含 displayPreferences/avatarUrl 等），
          // 立刻拉一次完整 profile，让 ThemeSync（主题偏好）与头像等全局组件生效。
          void get().refreshUser();
          return { twoFactorRequired: false };
        } catch (error) {
          set({ error: (error as Error).message, isLoading: false });
          throw error;
        }
      },

      verify2fa: async (challengeToken: string, code?: string, recoveryCode?: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await fetch('/api/auth/login/2fa', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ challengeToken, code, recoveryCode }),
            credentials: 'include',
          });
          if (!response.ok) {
            throw new ApiError(response.status, await response.json());
          }
          const data = await response.json();
          set({ user: data.user, token: data.token, isAuthenticated: true, isLoading: false });
          // login/register 返回的是精简 user（不含 displayPreferences/avatarUrl 等），
          // 立刻拉一次完整 profile，让 ThemeSync（主题偏好）与头像等全局组件生效。
          void get().refreshUser();
        } catch (error) {
          set({ error: (error as Error).message, isLoading: false });
          throw error;
        }
      },

      register: async (email: string, password: string, name: string, code: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await fetch('/api/auth/register', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            // 条款勾选由注册表单守卫,store 恒 true(consent 记录在 API 侧)
            body: JSON.stringify({ email, password, name, verificationCode: code, termsAccepted: true }),
            credentials: 'include',
          });

          if (!response.ok) {
            throw new ApiError(response.status, await response.json());
          }

          const data = await response.json();
          set({ user: data.user, token: data.token, isAuthenticated: true, isLoading: false });
          // login/register 返回的是精简 user（不含 displayPreferences/avatarUrl 等），
          // 立刻拉一次完整 profile，让 ThemeSync（主题偏好）与头像等全局组件生效。
          void get().refreshUser();
        } catch (error) {
          set({ error: (error as Error).message, isLoading: false });
          throw error;
        }
      },

      sendVerificationCode: async (email: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await fetch('/api/auth/send-verification', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.message || 'Failed to send verification code');
          }
        } catch (error) {
          set({ error: (error as Error).message });
          throw error;
        } finally {
          set({ isLoading: false });
        }
      },

      loginWithOAuthCode: async (code: string) => {
        // 原始 fetch 而非 apiFetch：code 无效返回 401 时不应触发全局自动登出
        const response = await fetch('/api/auth/oauth/complete', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code }),
        });
        if (!response.ok) {
          throw new ApiError(response.status, await response.json());
        }
        const data = await response.json();
        if (data.mode === 'bind') {
          return { mode: 'bind' as const, identity: data.identity as { id: string; provider: string } };
        }
        set({ user: data.user, token: data.token, isAuthenticated: true, isLoading: false });
        // 与 login/register 一致：立即拉完整 profile（主题/头像等全局组件生效）
        void get().refreshUser();
        return { mode: 'login' as const, isNewUser: Boolean(data.isNewUser) };
      },

      logout: async () => {
        // best-effort 吊销当前登录会话（POST /api/auth/logout）。用原始 fetch，避免触发
        // apiFetch 的 401 自动登出循环。失败不阻塞——前端照常清状态。
        const token = get().token;
        try {
          await fetch('/api/auth/logout', {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
        } catch {
          // 静默
        }
        set({ user: null, token: null, isAuthenticated: false, error: null });
      },

      clearError: () => set({ error: null }),
      setUser: (user) => set({ user }),
      refreshUser: async () => {
        const token = get().token;
        if (!token) return;
        try {
          const response = await fetch('/api/users/profile', {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (response.ok) {
            set({ user: await response.json() });
          }
        } catch {
          // silent — keep existing user on failure
        }
      },
    }),
    {
      name: 'clawrent-auth',
      // 同时持久化 token、user 和 isAuthenticated，刷新后状态完整恢复
      partialize: (state) => ({ token: state.token, user: state.user, isAuthenticated: state.isAuthenticated }),
    }
  )
);
